// File: src/Math/Grade7/Grade7MathLesson.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import "./Grade7MathLesson.css";
import WritingPad   from "../MathTools/WritingPad";
import Calculator   from "../MathTools/Calculator";
import LessonUI     from "../MathPagesStructure/LessonUI";
import {
  StoryModeQuiz,
  QuizResultScreen,
  FinalQuizUI as StoryFinalQuizUI,
} from "../MathPagesStructure/StoryModeStructure";
import { mathChapterPages } from "../MathLessonPools";
import { generateQuizQuestion } from "./Grade7QuizGenerator";

/* ===============================
   ALWAYS USE LOCALSTORAGE USER!
   =============================== */

const chapterTopicMap = {
  1: "rationalNumbers",
  2: "equations",
  3: "proportions",
  4: "geometry",
  5: "probability",
  6: null, // summary
};

export default function Grade7MathLesson({ onAwardTokens }) {
  // ----------- 取 user (同步 localStorage, not prop) ----------
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("loggedInUser");
    return raw ? JSON.parse(raw) : {};
  });

  // ----------- 章節/頁面進度 -----------
  const [chapter, setChapter] = useState(1);
  const [pageIdx, setPageIdx] = useState(0);

  // ----------- 小節 Quiz 狀態 -----------
  const [showChQ,  setShowChQ]  = useState(false);
  const [chQuiz,   setChQuiz]   = useState([]);
  const [chIdx,    setChIdx]    = useState(0);
  const [chScore,  setChScore]  = useState(0);
  const [chDone,   setChDone]   = useState(false);

  // ----------- Final Quiz 狀態 -----------
  const [showFinal, setShowFinal] = useState(false);
  const [finalQs,   setFinalQs]   = useState([]);
  const [fIdx,      setFIdx]      = useState(0);
  const [fScore,    setFScore]    = useState(0);
  const [fDone,     setFDone]     = useState(false);
  const tokenWritten               = useRef(false);

  // ----------- 小工具 -----------
  const [showCalc, setShowCalc] = useState(false);
  const [showPad,  setShowPad ] = useState(false);

  // ----------- fraction demo -----------
  const [fracA, setFracA]   = useState("1/2");
  const [fracB, setFracB]   = useState("1/3");
  const [fracRes, setFracRes] = useState("");

  // ----------- completion -----------
  const [allDone, setAllDone] = useState(false);
  const [hasPrevCompletion, setPrevComp] = useState(false);

  // ----------- 完成紀錄: localStorage ----------
  useEffect(() => {
    const done = localStorage.getItem("grade7MathComplete");
    if (done === "true") {
      setAllDone(true);
      setPrevComp(true);
    }
  }, []);
  useEffect(() => {
    if (allDone) localStorage.setItem("grade7MathComplete", "true");
  }, [allDone]);

  // ----------- 頁面 & Progress ----------
  const chapterPages = useMemo(() => mathChapterPages[7] ?? {}, []);
  const pages        = chapterPages[chapter] ?? [];
  const totalSlides  = useMemo(
    () => Object.values(chapterPages).reduce((n, arr) => n + arr.length, 0) || 1,
    [chapterPages]
  );
  const slidesSeen   = useMemo(() => {
    let s = 0;
    for (let c = 1; c < chapter; c++) s += (chapterPages[c]?.length || 0);
    return s + pageIdx;
  }, [chapter, pageIdx, chapterPages]);
  const progress = Math.round(((slidesSeen + 1) / totalSlides) * 100);

  // ----------- helper: next/prev/jump -----------
  const nextPage = () => pageIdx < pages.length - 1 && setPageIdx(i => i + 1);
  const prevPage = () => pageIdx > 0 && setPageIdx(i => i - 1);
  const jumpTo   = ch => { setChapter(ch); setPageIdx(0); setShowChQ(false); setShowFinal(false); };

  // ----------- 小節 Quiz Flow -----------
  const startChQ = () => {
    const topic = chapterTopicMap[chapter];
    if (!topic) { setAllDone(true); return; }
    setChQuiz(Array.from({ length: 10 }, () => generateQuizQuestion(topic)));
    setChIdx(0); setChScore(0); setChDone(false); setShowChQ(true);
  };
  const answerChQ = opt => {
    if (opt === chQuiz[chIdx].correctAnswer) setChScore(s => s + 1);
    chIdx < chQuiz.length - 1 ? setChIdx(i => i + 1) : setChDone(true);
  };
  const finishChQ = () => {
    if (chScore >= 6) {
      chapter < 6 ? setChapter(c => c + 1) : setAllDone(true);
      setPageIdx(0);
    }
    setShowChQ(false);
  };

  // ----------- Final Quiz Flow -----------
  const startFinal = () => {
    const used = new Set(), arr = [];
    while (arr.length < 50) {
      const q = generateQuizQuestion(), key = q.question + "|" + q.correctAnswer;
      if (!used.has(key)) { used.add(key); arr.push(q); }
    }
    setFinalQs(arr); setFIdx(0); setFScore(0); setFDone(false); setShowFinal(true);
    tokenWritten.current = false;
  };
  const answerFinal = opt => {
    if (opt === finalQs[fIdx].correctAnswer) setFScore(s => s + 1);
    fIdx < finalQs.length - 1 ? setFIdx(i => i + 1) : setFDone(true);
  };

  // ----------- award coins: 本地版 -----------
  useEffect(() => {
    if (!fDone || tokenWritten.current) return;
    const passed = (fScore / finalQs.length) * 100 >= 80;
    // "award" only coins in localStorage and UI (optionally: call API sync)
    if (passed) {
      // 本地 user coins
      setUser(prev => {
        const newUser = { ...(prev || {}), coins: (prev?.coins || 0) + 1 };
        localStorage.setItem("loggedInUser", JSON.stringify(newUser));
        return newUser;
      });
      if (onAwardTokens) onAwardTokens(1);
    }
    tokenWritten.current = true;
  }, [fDone, fScore, finalQs.length, onAwardTokens]);

  // ----------- 頭像組裝 (Grade3/4/6同邏輯) -----------
  const avatar =
    user?.avatar ||
    user?.photoURL ||
    `${process.env.PUBLIC_URL}/images/Student.png`;
  const quizUser = {
    ...user,
    avatar,
    profilePic: avatar,
  };

  // ----------- Fraction Demo -----------
  const doFracOp = op => {
    const [na, da] = fracA.split("/").map(Number);
    const [nb, db] = fracB.split("/").map(Number);
    if (!da || !db) return setFracRes("Invalid");
    const resTop = op === "add" ? na*db + nb*da : na*db - nb*da;
    setFracRes(`${resTop}/${da*db}`);
  };

  // ----------- 小節 Quiz 片段 -----------
  const ChQuizFrag = !chDone
    ? (
        <StoryModeQuiz
          questions    ={chQuiz}
          currentIndex ={chIdx}
          score        ={chScore}
          onAnswer     ={answerChQ}
          user         ={quizUser}
          tools        ={(
            <>
              <button onClick={() => setShowCalc(c => !c)}>{showCalc ? "Hide" : "Show"} Calculator</button>
              <button onClick={() => setShowPad (p => !p)}>{showPad  ? "Hide" : "Show"} Pad</button>
              {showCalc && <Calculator />}
              {showPad  && <WritingPad />}
            </>
          )}
        />
      )
    : (
        <QuizResultScreen
          score      ={chScore}
          total      ={chQuiz.length}
          onContinue ={finishChQ}
        />
      );

  // ----------- Final Quiz 片段 -----------
  const FinalFrag = !fDone
    ? (
        <StoryFinalQuizUI
          questions    ={finalQs}
          currentIndex ={fIdx}
          score        ={fScore}
          onAnswer     ={answerFinal}
          user         ={quizUser}
          onReturn     ={() => setShowFinal(false)}
        />
      )
    : (
        <QuizResultScreen
          score         ={fScore}
          total         ={finalQs.length}
          onContinue    ={() => setShowFinal(false)}
          continueLabel ="Return to Lesson"
        />
      );

  // ----------- 主 render -----------
  if (!pages.length) {
    return <p style={{ color: "red", textAlign: "center" }}>
      No pages found for Grade 7, chapter {chapter}.  Check MathLessonPools.js.
    </p>;
  }

  return (
    <div className="grade7-math-page">
      <div className="grade7-math-page-scale">
        {/* progress bar */}
        <div className="progress-wrapper">
          <div className="progress-bg">
            <div className="progress-fg" style={{ width: `${progress}%` }} />
          </div>
          <p>{progress}% complete</p>
        </div>

        {/* jump nav */}
        {hasPrevCompletion && !showFinal && !showChQ && (
          <div className="jump-ui">
            <p>Completed before – jump anywhere or retake final:</p>
            {[1,2,3,4,5].map(ch => (
              <button key={ch} onClick={() => jumpTo(ch)} style={{ margin: 4 }}>
                Chapter {ch}
              </button>
            ))}
            <button onClick={startFinal}>Final Quiz</button>
          </div>
        )}

        {/* lesson or chapter quiz */}
        {!showChQ && !showFinal && (
          <LessonUI
            pages             ={pages}
            currentPageIndex  ={pageIdx}
            onPrevPage        ={prevPage}
            onNextPage        ={nextPage}
            onStartQuiz       ={startChQ}
            backTo            ="/math"
            renderExtraSection={({ page }) => {
              if (page.isRationalInteractive) {
                return (
                  <div style={{ marginTop: 12 }}>
                    <input value={fracA} onChange={e => setFracA(e.target.value)} style={{ width: 70, marginRight: 6 }} />
                    <input value={fracB} onChange={e => setFracB(e.target.value)} style={{ width: 70, marginRight: 6 }} />
                    <button onClick={() => doFracOp("add")} style={{ marginRight: 6 }}>Add</button>
                    <button onClick={() => doFracOp("sub")}>Subtract</button>
                    {fracRes && <p style={{ marginTop: 6 }}>Result: {fracRes}</p>}
                  </div>
                );
              }
              return null;
            }}
          />
        )}
        {showChQ   && ChQuizFrag}

        {/* final prompt */}
        {allDone && !showFinal && !showChQ && (
          <div style={{ textAlign: "center" }}>
            <h3>All Chapters Complete!</h3>
            <button className="lesson-nav-btn" onClick={startFinal}>Start Final Quiz</button>
          </div>
        )}

        {showFinal && FinalFrag}
      </div>
    </div>
  );
}
